import type { PixelConfig } from '@/config';
import type { BehaviorModule } from '@/modules/behavior/behavior.module';
import type { ConversionModule } from '@/modules/conversion/conversion.module';
import type { IntegrationsModule } from '@/modules/integrations/integrations.module';
import type { TransportModule } from '@/transport/transport.module';
import type { PixelRuntime } from '@/runtime/PixelRuntime';

import { buildDebugSnapshot } from './SnapshotBuilder';
import { redactSensitiveKeys } from './sanitize';
import type { PixelDebugSnapshot, SnapshotCollectContext } from './types';

export type DebugSnapshotDeps = {
  getSdkVersion: () => string;
  getRuntime: () => PixelRuntime;
  getTransportModule: () => TransportModule;
  getConversionModule: () => ConversionModule;
  getIntegrationsModule: () => IntegrationsModule;
  getBehaviorModule: () => BehaviorModule;
  getConfig: () => PixelConfig;
  getRegisteredModules: () => ReadonlyArray<{
    name: string;
    configuredEnabled: boolean;
  }>;
};

/**
 * Orquestra coleta read-only → build → sanitize → freeze.
 * Sem timers, sem listeners, sem mutação de Runtime.
 */
export class DebugSnapshotService {
  private readonly deps: DebugSnapshotDeps;

  private destroyed = false;

  constructor(deps: DebugSnapshotDeps) {
    this.deps = deps;
  }

  markDestroyed(): void {
    this.destroyed = true;
  }

  markAlive(): void {
    this.destroyed = false;
  }

  getSnapshot(): Readonly<PixelDebugSnapshot> {
    return buildDebugSnapshot(this.collect());
  }

  private collect(): SnapshotCollectContext {
    const runtime = this.deps.getRuntime();
    const config = this.deps.getConfig();
    const services = runtime.getDebugServices();
    const transportModule = this.deps.getTransportModule();
    const conversionModule = this.deps.getConversionModule();
    const integrationsModule = this.deps.getIntegrationsModule();
    const transportState = transportModule.getDebugState();
    const registered = this.deps.getRegisteredModules();
    const initializedNames = new Set(runtime.getInitializedModuleNames());

    const moduleRegistry = registered.map(m => ({
      name: m.name,
      configuredEnabled: m.configuredEnabled,
      initialized: initializedNames.has(m.name),
    }));

    let servicesBag: SnapshotCollectContext['services'] = null;
    if (services) {
      const urlSnap = services.urlContext.isEnabled()
        ? services.urlContext.getSnapshot()
        : null;
      const commerceSnap = services.commerce.state.getSnapshot();
      const catalogProducts = services.commerce.catalog.list();
      const items = commerceSnap.selectedItems ?? [];
      let cartValue: number | null = 0;
      for (const item of items) {
        const fromItem =
          typeof item.price === 'number' ? item.price : undefined;
        const fromCatalog = catalogProducts.find(
          p => p.id === item.productId
        )?.price;
        const price = fromItem ?? fromCatalog ?? 0;
        cartValue += price * (item.quantity ?? 1);
      }

      const attrSnap = services.attribution.getSnapshot();
      const identifiers: Record<string, string> = {};
      for (const [k, v] of Object.entries(attrSnap)) {
        if (typeof v === 'string' && v.length > 0) identifiers[k] = v;
      }

      const sessionTtl = config.modules.core.session.ttlMs;

      servicesBag = {
        visitorId: services.visitor.getVisitorId(),
        sessionId: services.session.getSessionId(),
        sessionExpired: services.session.isExpired(sessionTtl),
        fingerprint: services.fingerprint.getFingerprint(),
        urlContextEnabled: services.urlContext.isEnabled(),
        urlContextSnapshot: urlSnap
          ? {
              managed: { ...(urlSnap.urlContext.managed ?? {}) },
              forwarded: { ...(urlSnap.urlContext.forwarded ?? {}) },
              source: {
                ...((urlSnap.urlContext.source as Record<string, string>) ??
                  {}),
              },
              queryParamCount: Object.keys(urlSnap.queryParams).length,
            }
          : null,
        attributionEnabled: config.attribution?.enabled !== false,
        attributionIdentifiers: identifiers,
        commerceEnabled: services.commerce.isEnabled(),
        commerceMode: config.commerce?.cart?.mode ?? null,
        commerceCurrency: config.commerce?.currency ?? null,
        catalogSize: catalogProducts.length,
        itemCount: commerceSnap.count,
        cartValue,
        productIds: items.map(i => i.productId).filter(Boolean),
      };
    }

    const trackers = config.modules.behavior.trackers;
    const heartbeatCfg = config.modules.behavior.heartbeat;
    const engagementCfg = config.modules.behavior.engagement;
    const viewportCfg = trackers?.viewport;
    const behaviorTrackers: string[] = [];
    if (trackers?.scroll?.enabled) behaviorTrackers.push('scroll');
    if (trackers?.click?.enabled) behaviorTrackers.push('click');
    if (trackers?.visibility?.enabled) behaviorTrackers.push('visibility');
    if (trackers?.idle?.enabled) behaviorTrackers.push('idle');
    if (trackers?.exitIntent?.enabled) behaviorTrackers.push('exitIntent');
    if (viewportCfg?.enabled === true) behaviorTrackers.push('viewport');

    const behaviorEngines: string[] = [];
    if (heartbeatCfg?.enabled === true) behaviorEngines.push('heartbeat');
    if (engagementCfg?.enabled === true) {
      behaviorEngines.push('engagement');
    }
    if (config.modules.behavior.rateLimit?.enabled === true) {
      behaviorEngines.push('rateLimit');
    }
    if (config.modules.behavior.buffer?.enabled === true) {
      behaviorEngines.push('buffer');
    }
    if (config.modules.behavior.stateMachine?.enabled === true) {
      behaviorEngines.push('stateMachine');
    }
    if (config.modules.behavior.sessionSummary?.enabled === true) {
      behaviorEngines.push('sessionSummary');
    }

    const behaviorModule = this.deps.getBehaviorModule();
    const heartbeatSnap = behaviorModule.getHeartbeatSnapshot() ?? null;
    const behaviorHeartbeat =
      heartbeatSnap ??
      (heartbeatCfg
        ? {
            enabled: heartbeatCfg.enabled === true,
            initialized: false,
            running: false,
            intervalMs: heartbeatCfg.intervalMs ?? 15_000,
            hiddenIntervalMs: heartbeatCfg.hiddenIntervalMs ?? 60_000,
            sequence: 0,
            emittedCount: 0,
            skippedCount: 0,
            lastEmittedAt: undefined as number | undefined,
            nextExpectedAt: undefined as number | undefined,
            lastDriftMs: undefined as number | undefined,
          }
        : null);

    const viewportSnap = behaviorModule.getViewportSnapshot() ?? null;
    const behaviorViewport =
      viewportSnap ??
      (viewportCfg
        ? {
            enabled: viewportCfg.enabled === true,
            initialized: false,
            available: false,
            targetCount: viewportCfg.targets?.length ?? 0,
            matchedElementCount: 0,
            observedElementCount: 0,
            activeElementCount: 0,
            pendingElementCount: 0,
            enterCount: 0,
            exitCount: 0,
            mutationObserverEnabled: false,
            maxObservedElements: viewportCfg.maxObservedElements ?? 500,
            limitReached: false,
            lastEventAt: undefined as number | undefined,
          }
        : null);

    const engagementSnap = behaviorModule.getEngagementSnapshot() ?? null;
    const behaviorEngagement =
      engagementSnap ??
      (engagementCfg
        ? {
            enabled: engagementCfg.enabled === true,
            initialized: false,
            running: false,
            score: 0,
            previousScore: 0,
            peakScore: 0,
            averageScore: 0,
            level: 'low' as const,
            evaluationCount: 0,
            emittedUpdateCount: 0,
            emittedSummaryCount: 0,
            lastEvaluatedAt: undefined as number | undefined,
            lastEmittedAt: undefined as number | undefined,
            evaluationIntervalMs: engagementCfg.evaluationIntervalMs ?? 5_000,
            lastDriftMs: undefined as number | undefined,
            hasError: false,
            signalCounts: {
              HEARTBEAT: 0,
              SCROLL: 0,
              CLICK: 0,
              RAGE_CLICK: 0,
              IDLE: 0,
              ACTIVITY: 0,
              VISIBLE: 0,
              HIDDEN: 0,
              VIEWPORT_ENTER: 0,
              VIEWPORT_EXIT: 0,
            },
          }
        : null);

    const rateLimitCfg = config.modules.behavior.rateLimit;
    const bufferCfg = config.modules.behavior.buffer;
    const stateMachineCfg = config.modules.behavior.stateMachine;
    const sessionSummaryCfg = config.modules.behavior.sessionSummary;

    const behaviorRateLimit =
      behaviorModule.getRateLimitSnapshot() ??
      (rateLimitCfg
        ? {
            enabled: rateLimitCfg.enabled === true,
            windowMs: rateLimitCfg.windowMs ?? 1_000,
            maxSignalsPerWindow: rateLimitCfg.maxSignalsPerWindow ?? 50,
            criticalBypass: rateLimitCfg.criticalBypass !== false,
            accepted: 0,
            dropped: 0,
            bypassed: 0,
            windowCount: 0,
            windowStartedAt: undefined as number | undefined,
          }
        : null);

    const behaviorBuffer =
      behaviorModule.getBufferSnapshot() ??
      (bufferCfg
        ? {
            enabled: bufferCfg.enabled === true,
            size: 0,
            maxSize: bufferCfg.maxSize ?? 200,
            flushSize: bufferCfg.flushSize ?? 50,
            maxAgeMs: bufferCfg.maxAgeMs ?? 30_000,
            dropStrategy: bufferCfg.dropStrategy ?? 'compress_similar',
            flushCount: 0,
            dropCount: 0,
            compressedCount: 0,
            lastFlushAt: undefined as number | undefined,
            lastFlushReason: undefined as
              | 'size'
              | 'age'
              | 'hidden'
              | 'destroy'
              | 'manual'
              | undefined,
          }
        : null);

    const behaviorStateMachine =
      behaviorModule.getStateMachineSnapshot() ??
      (stateMachineCfg
        ? {
            enabled: stateMachineCfg.enabled === true,
            state: 'NOT_INITIALIZED',
            previousState: undefined as string | undefined,
            generation: 0,
            changedAt: undefined as number | undefined,
            transitionCount: 0,
            invalidTransitionCount: 0,
            historySize: 0,
          }
        : null);

    const behaviorSessionSummary =
      behaviorModule.getSessionSummarySnapshot() ??
      (sessionSummaryCfg
        ? {
            enabled: sessionSummaryCfg.enabled === true,
            emitOnHidden: sessionSummaryCfg.emitOnHidden === true,
            emitOnDestroy: sessionSummaryCfg.emitOnDestroy !== false,
            minimumSessionMs: sessionSummaryCfg.minimumSessionMs ?? 0,
            emittedCount: 0,
            lastEmittedAt: undefined as number | undefined,
            startedAt: undefined as number | undefined,
          }
        : null);

    const manual = config.modules.conversion.manual;
    const manualEnabledFlags: Record<string, boolean> = {
      lead: manual.lead?.enabled === true,
      addToCart: manual.addToCart?.enabled === true,
      removeFromCart: manual.removeFromCart?.enabled === true,
      initiateCheckout: manual.initiateCheckout?.enabled === true,
      addPaymentInfo: manual.addPaymentInfo?.enabled === true,
      purchase: manual.purchase?.enabled === true,
      viewContent: manual.viewContent?.enabled === true,
      search: manual.search?.enabled === true,
    };

    const detectors = config.modules.conversion.auto?.detectors ?? {};
    const detectorCount = Object.values(detectors).filter(
      d => d && typeof d === 'object' && (d as { enabled?: boolean }).enabled
    ).length;

    const integrationState = integrationsModule.getDebugState();
    const delivery = transportState?.delivery;
    const advanced = transportState?.advanced;

    return {
      sdkVersion: this.deps.getSdkVersion(),
      runtimeInitialized: runtime.isInitialized(),
      runtimeDestroyed: this.destroyed,
      moduleRegistry,
      services: servicesBag,
      behaviorEnabled: config.modules.behavior.enabled === true,
      behaviorTrackers,
      behaviorEngines,
      behaviorHeartbeat,
      behaviorViewport,
      behaviorEngagement,
      behaviorRateLimit,
      behaviorBuffer,
      behaviorStateMachine,
      behaviorSessionSummary,
      conversion: {
        enabled: config.modules.conversion.enabled === true,
        autoEnabled: config.modules.conversion.auto?.enabled === true,
        manualEnabledFlags,
        detectorCount,
        activeDetectors: [...conversionModule.getDebugActiveDetectors()],
        dedupeKeyCount: conversionModule.getDebugDedupeKeys().length,
      },
      integrations: {
        enabled: config.modules.integrations.enabled === true,
        active: integrationState?.active === true,
        destinationNames: [...(integrationState?.destinationNames ?? [])],
        metrics: (integrationState?.metrics as Record<string, unknown>) ?? null,
      },
      transport: {
        enabled: config.transport.enabled === true,
        state: transportState
          ? {
              online: delivery?.offline?.isOnline ?? 'unknown',
              senderMode: advanced?.senderMode ?? 'unknown',
              queueSize: transportState.queueSize,
              sentCount: transportState.sentCount,
              ackCount: delivery?.ackCount ?? 0,
              inFlight: delivery?.inFlight ?? false,
              retryPending: delivery?.retry?.pendingCount ?? 0,
              circuitBreakerState: String(
                advanced?.circuitBreaker?.state ?? 'unavailable'
              ),
              dlqCount: advanced?.deadLetter?.count ?? 0,
              isLeader: advanced?.coordination?.isLeader ?? true,
              persistenceEnabled:
                advanced?.storage?.persistenceEnabled ?? false,
            }
          : null,
      },
      configSafe: buildSafeConfig(config),
    };
  }
}

function buildSafeConfig(config: PixelConfig): Record<string, unknown> {
  const raw = {
    app: {
      environment: config.app.environment,
      debug: config.app.debug,
      source: config.app.source,
      target: config.app.target,
      platform: config.app.platform,
    },
    identity: {
      workspaceId: config.identity.workspaceId ? '[Present]' : '[Missing]',
      pixelId: config.identity.pixelId ? '[Present]' : '[Missing]',
    },
    transport: {
      enabled: config.transport.enabled,
      sender: {
        mode: config.transport.sender.mode,
      },
    },
    modules: {
      core: { enabled: config.modules.core.enabled },
      behavior: { enabled: config.modules.behavior.enabled },
      conversion: {
        enabled: config.modules.conversion.enabled,
        auto: { enabled: config.modules.conversion.auto?.enabled },
      },
      integrations: { enabled: config.modules.integrations.enabled },
      privacy: { enabled: config.modules.privacy.enabled },
    },
    commerce: {
      enabled: config.commerce?.enabled,
      currency: config.commerce?.currency,
      cartMode: config.commerce?.cart?.mode,
    },
    attribution: { enabled: config.attribution?.enabled },
  };
  return redactSensitiveKeys(raw as Record<string, unknown>);
}
